import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Shield, Smartphone, Clock, Trash2, X, Check, AlertCircle, BookOpen, Monitor } from "lucide-react";

/**
 * Privacy Center — GDPR transparency dashboard.
 * Shows: consent history, legal-acceptance history, active sessions (revoke), 2FA status.
 * Mounted at /me/privacy.
 */
export default function PrivacyCenter() {
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  function show(m) { setToast(m); setTimeout(() => setToast(""), 2500); }

  async function load() {
    setLoading(true);
    try {
      const [pc, ses] = await Promise.all([
        api.get("/me/privacy-center"),
        api.get("/me/sessions"),
      ]);
      setData(pc.data);
      setSessions(ses.data || []);
    } catch (e) {
      show(formatErr(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function revokeAll() {
    if (!window.confirm("Disconnetti tutti gli altri dispositivi? La sessione corrente resta attiva.")) return;
    try {
      const { data } = await api.post("/me/sessions/revoke-all");
      show(`${data.revoked} sessioni disconnesse`);
      load();
    } catch (e) {
      show(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  async function revokeOne(jti) {
    try {
      await api.post(`/me/sessions/${jti}/revoke`);
      show("Sessione disconnessa");
      load();
    } catch (e) {
      show(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;
  }
  if (!data) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 space-y-10" data-testid="privacy-center">
      <div>
        <Link to="/me" className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100">← Torna al profilo</Link>
        <div className="mt-3 text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 inline-flex items-center gap-2">
          <Shield className="h-3.5 w-3.5" /> GDPR · Centro Privacy
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1">I tuoi dati e la tua sicurezza</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl dark:text-zinc-400">
          Trasparenza completa: consensi dati, accettazione documenti legali, dispositivi attivi e autenticazione a 2 fattori.
        </p>
      </div>

      {/* Active sessions */}
      <Section title="Dispositivi attivi" icon={Monitor} count={sessions.length}>
        <div className="space-y-2">
          {sessions.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessuna sessione attiva.</div>}
          {sessions.map((s) => (
            <div key={s.jti} className={`rounded-2xl ring-1 ring-zinc-100 p-4 flex items-start gap-3 ${s.is_current ? "bg-emerald-50/50 dark:bg-emerald-950/40" : "bg-white"}`} data-testid={`session-${s.jti.slice(0,8)}`}>
              <Smartphone className="h-5 w-5 mt-0.5 text-zinc-400 shrink-0 dark:text-zinc-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{(s.user_agent || "").slice(0, 80) || "Dispositivo sconosciuto"}</div>
                <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
                  IP {s.ip || "?"} · creata {fmtDate(s.created_at)}
                  {s.last_seen_at && <> · ultimo accesso {fmtDate(s.last_seen_at)}</>}
                </div>
                {s.is_current && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold dark:text-emerald-300">Sessione corrente</span>}
              </div>
              {!s.is_current && (
                <button onClick={() => revokeOne(s.jti)} className="text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300" data-testid={`session-revoke-${s.jti.slice(0,8)}`}>
                  Disconnetti
                </button>
              )}
            </div>
          ))}
        </div>
        {sessions.length > 1 && (
          <button onClick={revokeAll} className="mt-3 btn-secondary h-10 text-sm" data-testid="revoke-all-btn">
            Disconnetti tutti gli altri dispositivi
          </button>
        )}
      </Section>

      {/* 2FA */}
      <Section title="Autenticazione a 2 fattori" icon={Shield}>
        {data.totp_enabled ? (
          <div className="flex items-start gap-3 rounded-2xl ring-1 ring-emerald-200 bg-emerald-50/40 p-4 dark:bg-emerald-950/40">
            <Check className="h-5 w-5 mt-0.5 text-emerald-600" />
            <div className="text-sm">
              <div className="font-medium">2FA attivo</div>
              <div className="text-xs text-zinc-600 dark:text-zinc-400">Il tuo account richiede un codice TOTP al login. Per disattivarlo contatta il supporto.</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-600 dark:text-zinc-400">
            Disponibile solo per account amministratore. Per attivarla, dalla console admin segui la procedura "Sicurezza → Abilita 2FA".
          </div>
        )}
      </Section>

      {/* Legal acceptance history */}
      <Section title="Accettazione documenti legali" icon={BookOpen} count={data.legal_acceptance_history?.length}>
        {(!data.legal_acceptance_history || data.legal_acceptance_history.length === 0) ? (
          <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessuna accettazione registrata. I documenti restano consultabili: <Link to="/privacy" className="text-blue-600 hover:underline">Privacy</Link>, <Link to="/terms" className="text-blue-600 hover:underline">Termini</Link>, <Link to="/cookie-policy" className="text-blue-600 hover:underline">Cookie</Link>, <Link to="/dpa" className="text-blue-600 hover:underline">DPA</Link>.</div>
        ) : (
          <div className="space-y-2">
            {data.legal_acceptance_history.map((r, i) => (
              <Row key={i} title={`${labelDoc(r.doc_type)} · v${r.version}`} subtitle={`${fmtDate(r.accepted_at)} · IP ${r.ip || "?"}`} />
            ))}
          </div>
        )}
      </Section>

      {/* Consent log */}
      <Section title="Storico consensi" icon={Clock} count={data.consents?.length}>
        {(!data.consents || data.consents.length === 0) ? (
          <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessun consenso registrato.</div>
        ) : (
          <div className="space-y-2">
            {data.consents.slice(0, 20).map((c, i) => (
              <Row key={i} title={`${c.consent_type || "consent"} · ${c.context || "—"}`} subtitle={`${fmtDate(c.created_at)} · v${c.doc_version || "1.0"} · IP ${c.ip || "?"}`} />
            ))}
            {data.consents.length > 20 && <div className="text-xs text-zinc-400 dark:text-zinc-500">+{data.consents.length - 20} consensi più vecchi non mostrati</div>}
          </div>
        )}
      </Section>

      {/* GDPR rights reminder */}
      <Section title="I tuoi diritti GDPR" icon={AlertCircle}>
        <div className="text-sm text-zinc-700 space-y-2 dark:text-zinc-300">
          <p>Puoi in qualsiasi momento:</p>
          <ul className="list-disc pl-5 space-y-1 text-zinc-600 dark:text-zinc-400">
            <li><strong>Esportare i tuoi dati</strong> in JSON dal profilo (art. 15/20 GDPR)</li>
            <li><strong>Cancellare l'account</strong> dal profilo (art. 17 — diritto all'oblio)</li>
            <li><strong>Rettificare</strong> i tuoi dati direttamente dal profilo</li>
            <li><strong>Reclamare</strong> presso il <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Garante Privacy</a></li>
          </ul>
          <p className="text-xs text-zinc-500 mt-3 dark:text-zinc-400">Per richieste scrivi a <a href="mailto:info@assiteclecce.it" className="text-blue-600 hover:underline">info@assiteclecce.it</a> — risposta entro 30 giorni (art. 12.3).</p>
        </div>
      </Section>

      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-sm px-4 py-2 rounded-full shadow-lg z-[70]" data-testid="privacy-center-toast">{toast}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-medium tracking-tight mb-3 inline-flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />}
        {title}
        {typeof count === "number" && <span className="text-xs text-zinc-400 font-normal tabular-nums dark:text-zinc-500">({count})</span>}
      </h2>
      <div>{children}</div>
    </section>
  );
}

function Row({ title, subtitle }) {
  return (
    <div className="rounded-xl ring-1 ring-zinc-100 bg-white px-4 py-3 text-sm dark:ring-zinc-800 dark:bg-zinc-900">
      <div className="font-medium">{title}</div>
      <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{subtitle}</div>
    </div>
  );
}

function labelDoc(t) {
  return { privacy: "Privacy Policy", cookie: "Cookie Policy", terms: "Termini di Servizio", dpa: "DPA" }[t] || t;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
